import * as Dialog from "@radix-ui/react-dialog";
import { useMemo } from "react";
import { formatNGN } from "@/utils/moneyFormatter";

/** Row from `data.data[]` in the penalty breakdown API. */
export interface PenaltyBreakdownEmployeeRow {
  employerCode?: string;
  forTheMonthOf?: string;
  yearOfContribution?: string;
  rsaPin?: string;
  nameOfEmployee?: string;
  penalty?: number;
  paidAmount?: number;
  outstandingAmount?: number;
  pfcCode?: string;
  dateOfContribution?: string;
  valueDate?: string;
  paymentDate?: string;
  daysLate?: number;
}

export interface PenaltyBreakdownSummary {
  employerCode?: string;
  totalPenalty?: number;
  totalOutstanding?: number;
  months?: Array<{
    month: string;
    monthName: string;
    rows: number;
    totalPenalty: number;
    totalOutstanding: number;
  }>;
}

function parsePenaltyBreakdownPayload(raw: unknown): {
  summary: PenaltyBreakdownSummary | null;
  rows: PenaltyBreakdownEmployeeRow[];
} {
  if (raw == null) return { summary: null, rows: [] };

  let payload: Record<string, unknown> | null = null;

  if (typeof raw === "object" && raw !== null) {
    const top = raw as Record<string, unknown>;
    // Envelope: { status, message, data: { summary, data } }
    if (
      top.data != null &&
      typeof top.data === "object" &&
      !Array.isArray(top.data)
    ) {
      payload = top.data as Record<string, unknown>;
    } else if ("summary" in top || Array.isArray(top.data)) {
      payload = top;
    }
  }

  if (!payload) return { summary: null, rows: [] };

  const summary =
    (payload.summary as PenaltyBreakdownSummary | undefined) ?? null;
  const rows = Array.isArray(payload.data)
    ? (payload.data as PenaltyBreakdownEmployeeRow[])
    : [];

  return { summary, rows };
}

export interface PenaltyEmployeesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  monthLabel: string;
  year: number;
  isLoading: boolean;
  error: Error | null;
  data: unknown;
}

export const PenaltyEmployeesDialog = ({
  open,
  onOpenChange,
  monthLabel,
  year,
  isLoading,
  error,
  data,
}: PenaltyEmployeesDialogProps) => {
  const rows = useMemo(() => {
    if (!open) return [];
    const parsed = parsePenaltyBreakdownPayload(data);
    return parsed.rows.filter(
      (row) => typeof row.penalty === "number" && row.penalty > 0
    );
  }, [open, data]);

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 z-50" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-[960px] max-h-[90vh] translate-x-[-50%] translate-y-[-50%] bg-white rounded-2xl shadow-lg overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 focus:outline-none flex flex-col">
          <div className="flex items-start justify-between p-6 pb-4 border-b border-stroke shrink-0">
            <div className="flex flex-col gap-y-2 pr-8">
              <Dialog.Title className="font-clash-display font-semibold text-[20px] text-[#121212] leading-normal">
                Penalty breakdown
              </Dialog.Title>
              <Dialog.Description className="font-clash-display font-medium text-sm text-[#737373]">
                {monthLabel} {year}
              </Dialog.Description>
            </div>
            <Dialog.Close className="rounded-sm opacity-70 hover:opacity-100 focus:outline-none transition-opacity cursor-pointer shrink-0 mt-0.5">
              <svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden
              >
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Dialog.Close>
          </div>

          <div className="flex-1 overflow-y-auto px-6 py-6 min-h-[120px] flex flex-col gap-y-6">
            {isLoading ? (
              <p className="font-clash-display text-sm text-[#737373]">
                Loading…
              </p>
            ) : error ? (
              <p className="font-clash-display text-sm text-dark-red">
                {error.message ||
                  "Could not load penalty breakdown for this period."}
              </p>
            ) : (
              <>
                {rows.length === 0 ? (
                  <p className="font-clash-display text-sm text-[#737373]">
                    No employee penalty records for this period.
                  </p>
                ) : (
                  <div className="overflow-x-auto rounded-lg border border-stroke">
                    <table className="w-full min-w-[760px] border-collapse text-left">
                      <thead>
                        <tr className="bg-[#FCFCF8] border-b border-stroke">
                          <th className="font-clash-display font-medium text-sm text-[#404040] px-4 py-3 border-r border-stroke">
                            Employee name
                          </th>
                          <th className="font-clash-display font-medium text-sm text-[#404040] px-4 py-3 border-r border-stroke">
                            RSA PIN
                          </th>
                          <th className="font-clash-display font-medium text-sm text-[#404040] px-4 py-3 border-r border-stroke">
                            PFA
                          </th>
                          <th className="font-clash-display font-medium text-sm text-[#404040] px-4 py-3 border-r border-stroke whitespace-nowrap">
                            Contribution Paid On
                          </th>
                          <th className="font-clash-display font-medium text-sm text-[#404040] px-4 py-3 border-r border-stroke whitespace-nowrap">
                            Days Late
                          </th>
                          <th className="font-clash-display font-medium text-sm text-[#404040] px-4 py-3 border-r border-stroke text-right whitespace-nowrap">
                            Assessed Penalty
                          </th>
                          <th className="font-clash-display font-medium text-sm text-[#404040] px-4 py-3 border-r border-stroke text-right whitespace-nowrap">
                            Paid Amount
                          </th>
                          <th className="font-clash-display font-medium text-sm text-[#404040] px-4 py-3 text-right whitespace-nowrap">
                            Outstanding Amount
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, rowIndex) => (
                          <tr
                            key={`${row.rsaPin ?? rowIndex}-${rowIndex}`}
                            className="border-b border-stroke last:border-b-0 bg-white"
                          >
                            <td className="font-clash-display font-medium text-sm text-[#404040] px-4 py-3 border-r border-stroke max-w-[220px]">
                              <span
                                className="line-clamp-2"
                                title={row.nameOfEmployee}
                              >
                                {row.nameOfEmployee ?? "—"}
                              </span>
                            </td>
                            <td className="font-clash-display font-normal text-sm text-[#404040] px-4 py-3 border-r border-stroke whitespace-nowrap">
                              {row.rsaPin ?? "—"}
                            </td>
                            <td className="font-clash-display font-normal text-sm text-[#404040] px-4 py-3 border-r border-stroke max-w-[180px]">
                              <span
                                className="line-clamp-2"
                                title={row.pfcCode}
                              >
                                {row.pfcCode ?? "—"}
                              </span>
                            </td>
                            <td className="font-clash-display font-normal text-sm text-[#404040] px-4 py-3 border-r border-stroke whitespace-nowrap">
                              {row.paymentDate ?? "—"}
                            </td>
                            <td className="font-clash-display font-normal text-sm text-[#404040] px-4 py-3 border-r border-stroke whitespace-nowrap">
                              {row.daysLate != null ? row.daysLate : "—"}
                            </td>
                            <td className="font-clash-display font-normal text-sm text-[#404040] px-4 py-3 border-r border-stroke text-right whitespace-nowrap tracking-[-0.084px]">
                              {typeof row.penalty === "number"
                                ? formatNGN(row.penalty)
                                : "—"}
                            </td>
                            <td className="font-clash-display font-normal text-sm text-[#404040] px-4 py-3 border-r border-stroke text-right whitespace-nowrap tracking-[-0.084px]">
                              {typeof row.paidAmount === "number"
                                ? formatNGN(row.paidAmount)
                                : "—"}
                            </td>
                            <td className="font-clash-display font-normal text-sm text-[#404040] px-4 py-3 text-right whitespace-nowrap tracking-[-0.084px]">
                              {typeof row.outstandingAmount === "number"
                                ? formatNGN(row.outstandingAmount)
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
